import type { Ingredient, InventoryCount, InventoryCountLine } from './types';
import { computeIngredientCost, ingredientDisplayName } from './costing';
import { addMonths, monthOf, type Period } from './period';
import { round } from './units';

/**
 * Inventario mensual: valoracion de recuentos y su lectura para la P&L.
 *
 * El recuento se hace en la UNIDAD DE COMPRA de cada ingrediente ("quedan
 * 12 kg de solomillo, 3 L de AOVE"), porque es como se piensa en el almacen,
 * y se valora con el precio medio de compra que ya vive en el ingrediente.
 * El valor se CONGELA al guardar: un cambio de precio posterior no reescribe
 * lo que valian las existencias de un mes cerrado.
 */

export interface InventoryLineInput {
  ingredientId: string;
  qty: number;
}

/**
 * Valora las lineas de un recuento con los precios vigentes.
 *
 * Un ingrediente sin precio cargado no tumba el recuento: se valora a 0 y el
 * que guarda ve el cero en pantalla (mejor un cero visible que un error que
 * impida cerrar el mes).
 */
export function valueInventoryLines(
  lines: InventoryLineInput[],
  ingredients: Ingredient[],
): { lines: InventoryCountLine[]; totalValue: number } {
  const byId = new Map(ingredients.map((i) => [i.ingredientId, i]));
  const valued: InventoryCountLine[] = [];

  for (const line of lines) {
    const ing = byId.get(line.ingredientId);
    if (!ing || !(line.qty > 0)) continue;

    let unitCost = 0;
    try {
      unitCost = computeIngredientCost(ing).costPerPurchaseUnit;
    } catch {
      /* sin precio: valor 0, visible en pantalla */
    }

    valued.push({
      ingredientId: ing.ingredientId,
      name: ingredientDisplayName(ing),
      qty: line.qty,
      unit: ing.purchaseUnit,
      value: round(line.qty * unitCost, 2),
    });
  }

  return {
    lines: valued,
    totalValue: round(valued.reduce((sum, l) => sum + l.value, 0), 2),
  };
}

/** Valor de las existencias a cierre de un mes (suma de centros, si hay varios). */
export function inventoryValueAt(counts: InventoryCount[], month: string): number | null {
  const delMes = counts.filter((c) => c.month === month);
  if (delMes.length === 0) return null;
  return round(delMes.reduce((sum, c) => sum + c.totalValue, 0), 2);
}

/**
 * Existencias iniciales y finales de un periodo.
 *
 * - Finales: el recuento del mes en que TERMINA el periodo.
 * - Iniciales: el recuento del mes ANTERIOR al que empieza (lo que quedaba en
 *   el almacen la noche antes de arrancar).
 *
 * Un extremo sin recuento cuenta como 0 con tal de que exista el otro: el
 * primer mes que se inventaria no tiene "iniciales" y su consumo es compras
 * menos lo que quedo en el estante, que es exactamente lo que se busca.
 */
export function inventoryForPeriod(
  counts: InventoryCount[],
  period: Period,
): { hasInventory: boolean; opening: number; closing: number } {
  const openingMonth = addMonths(monthOf(period.start), -1);
  const closingMonth = monthOf(period.end);
  const opening = inventoryValueAt(counts, openingMonth);
  const closing = inventoryValueAt(counts, closingMonth);
  return {
    hasInventory: opening != null || closing != null,
    opening: opening ?? 0,
    closing: closing ?? 0,
  };
}
